import { useCallback, useEffect, useRef } from "react";
import { usePostHog } from "@posthog/react";

import { Feature, Features, PaymentMethodOption, WalletType } from "common/types";
import { toDollarValue } from "common/utils/formatting";
import { ClientCartDetail, DeliveryGroupKind, ICartItem } from "common/types/carts";
import { Optional } from "common/utils/types";
import { useIntersectionObserver } from "common/utils/dom";

import { ClientCheckoutOrder } from "client/orders/types";
import { INITIAL_CLIENT_DATA } from "client/common/utils";

import {
  AnalyticsCartItem,
  AnalyticsItemData,
  AnalyticsItemEventData,
  AnalyticsProductData,
  AnalyticsProductLineData,
  CustomAnalyticsEventName,
  CustomAnalyticsEventType,
  isAnalyticsItemData,
  isAnalyticsProductData,
  EcommerceAddToCartEvent,
  EcommerceAddShippingInfoEvent,
  EcommerceAddPaymentInfoEvent,
  EcommerceBeginCheckoutEvent,
  EcommerceEventName,
  EcommerceEventType,
  EcommerceItem,
  EcommercePaymentType,
  EcommerceSelectItemEvent,
  EcommerceViewItemEvent,
  EcommerceViewItemListEvent,
  EcommercePurchaseEvent,
  EcommerceRemoveFromCartEvent,
  PostHogAddToCartEvent,
  PostHogBeginCheckoutEvent,
  PageViewData,
  PostHogCheckoutCompletedEvent,
  PostHogEventName,
  PostHogEventType,
  PostHogItemClickEvent,
  PostHogItemsViewEvent,
  PostHogAddToCartFromLinkEvent,
  PostHogProductClickEvent,
  PostHogProductLineClickEvent,
  PostHogProductLineViewEvent,
  PostHogProductLinesViewEvent,
  PostHogProductsViewEvent,
  PostHogRemoveFromCartEvent,
  PostHogUpdatePaymentMethodEvent,
  PostHogUpdateShippingMethodEvent,
  PostHogLoginAttemptedEvent,
  PostHogLoginFailedEvent,
  PostHogLoginSucceededEvent,
  PostHogPasswordResetRequestedEvent,
  PostHogPasswordResetRequestSucceededEvent,
  PostHogPasswordResetRequestFailedEvent,
  PostHogSignupCompletedEvent,
  PostHogSignupFailedEvent,
} from "../types";

/**
 * Clinic feature flags as PostHog experiment super properties:
 * `{ "$feature/<flag>": "test" | "control" }` per flag, where `<flag>` is the Feature value
 * verbatim (e.g. `PRODUCT_SEARCH_CARDS_V2`) — "test" when enabled, else "control" (PostHog's
 * default variant names). Spread into the analytics context; add more flags by
 * listing them: `...featureFlagProperties(clinic.features, Feature.SomeFlag, Feature.Other)`.
 */
export const featureFlagProperties = (
  features: Features,
  ...flags: Feature[]
): Record<string, "test" | "control"> =>
  Object.fromEntries(
    flags.map((flag): [string, "test" | "control"] => [
      `$feature/${flag}`,
      features[flag] ? "test" : "control",
    ]),
  );

export const getCartItemPricing = (cartItem: ICartItem): { price: number; discount: number } => ({
  price: cartItem.custom_unit_price ?? cartItem.default_unit_price,
  discount: cartItem.unit_discount,
});

/** Builds pricing fields from optional price/discount values. */
const pricingFields = (opts?: { price?: number; discount?: number }): Partial<EcommerceItem> => ({
  ...(opts?.price != null ? { price: toDollarValue(opts.price) } : {}),
  ...(opts?.discount != null ? { discount: toDollarValue(opts.discount) } : {}),
});

const productLineFields = (pl: AnalyticsProductLineData): Partial<EcommerceItem> => ({
  item_id: `${pl.id}`,
  item_name: pl.name,
  item_brand: pl.brand_name,
  item_category: pl.primary_category_name,
  item_category2: pl.categories
    .filter((c) => c.name !== pl.primary_category_name)
    .map((c) => c.name)
    .sort()
    .join(", "),
  prescription_required: pl.prescription_required,
  ...(pl.manufacturer ? { manufacturer_name: pl.manufacturer.name } : {}),
});

const productFields = (product: AnalyticsProductData): Partial<EcommerceItem> => ({
  ...productLineFields(product.product_line),
  product_id: `${product.id}`,
  product_name: product.name,
});

const itemFields = (data: AnalyticsItemData): Partial<EcommerceItem> => ({
  ...productFields(data.product),
  vetcove_item_id: `${data.id}`,
  vetcove_item_name: data.name,
  days_supply: data.days_supply,
  frequency: `${data.duration_value} ${data.duration_unit}`,
});

const toEcommerceItem = (
  data: AnalyticsItemEventData,
  opts?: { price?: number; discount?: number },
): EcommerceItem => {
  if (isAnalyticsItemData(data)) {
    return { ...itemFields(data), ...pricingFields(opts) };
  }
  if (isAnalyticsProductData(data)) {
    return { ...productFields(data), ...pricingFields(opts) };
  }
  return { ...productLineFields(data), ...pricingFields(opts) };
};

/**
 * Converts an AnalyticsCartItem to EcommerceItem.
 * Uses pre-computed price and discount from the cart item.
 */
const analyticsCartItemToEcommerceItem = (cartItem: AnalyticsCartItem): EcommerceItem => ({
  ...toEcommerceItem(cartItem.item, { price: cartItem.price, discount: cartItem.discount }),
  quantity: cartItem.quantity,
});

const initialClientData = INITIAL_CLIENT_DATA;

/**
 * Sends GA4 ecommerce events via `gtag()`. Events are processed by the gtag.js
 * library into the eventModel format GA4 and GTM tags expect — including the
 * nested ecommerce item shape — so prefer this over `sendDataLayerEvent` for
 * anything GA4 recognizes natively.
 *
 * Assumes the partner has gtag.js fully loaded for the clinic, either by
 * providing a GA measurement ID (loaded by c15t in ConsentProvider) or by
 * loading gtag as a tag inside their Google Tag Manager container. The stub
 * in react-bundle.html queues calls made before gtag.js initializes; gtag.js
 * replays them once it loads. Clinics without either path will silently drop
 * these events.
 *
 * For custom (non-GA4) events — e.g. page_view virtual pageviews — use
 * `sendDataLayerEvent` instead, which pushes to the dataLayer directly and does
 * not require gtag.js.
 *
 * Event names and data are defined by our partners' GTM setup.
 * Keep consistent with: https://docs.google.com/document/d/1sYGNv_llIEIvoFTq2Z9fX12oGn3BT46NgAlDYM0LsI0/
 * */
export const sendGtagEvent = <T extends EcommerceEventType<EcommerceEventName, object>>({
  name,
  data,
}: T) => {
  if (initialClientData.debug_analytics_events) {
    console.log(`[Analytics][GTM] ${name}`, data); // eslint-disable-line no-console
  }
  gtag("event", name, data);
};

/**
 * Sends custom (non-GA4) analytics events by pushing directly to the dataLayer.
 * The event name is prefixed with `vetcove_` (e.g. `page_view` →
 * `vetcove_page_view`) so it cannot collide with GA4 reserved event names or
 * other tags sharing the container.
 *
 * Unlike `sendGtagEvent`, this does not depend on gtag.js being loaded —
 * GTM reads the pushed object directly — making it the right choice for events
 * that partners wire up through GTM triggers rather than GA4 ecommerce.
 */
export const sendDataLayerEvent = <
  T extends CustomAnalyticsEventType<CustomAnalyticsEventName, object>,
>({
  name,
  data,
}: T) => {
  const namespacedEventName = `vetcove_${name}`;
  if (initialClientData.debug_analytics_events) {
    console.log(`[Analytics][GTM] ${namespacedEventName}`, data); // eslint-disable-line no-console
  }
  window.dataLayer.push({ event: namespacedEventName, ...data });
};

export const useSendPostHogEvent = () => {
  const posthog = usePostHog();

  return useCallback(
    <T extends PostHogEventType<PostHogEventName, object>>({ name, data }: T) => {
      if (initialClientData.debug_analytics_events) {
        console.log(`[Analytics][Posthog] ${name}`, data); // eslint-disable-line no-console
      }
      if (!initialClientData.has_internal_analytics_tracking || !window.POSTHOG_PUBLIC_API_KEY) {
        return;
      }
      posthog.capture(name, data);
    },
    [posthog],
  );
};

/**
 * Returns the current PostHog distinct id, or null when analytics is disabled.
 *
 * Used to hand a logged-out requester's anonymous distinct id to the backend so it can be
 * aliased to the client's identified id, joining pre- and post-identification events.
 */
export const useGetPostHogDistinctId = () => {
  const posthog = usePostHog();

  return useCallback((): string | null => {
    if (!initialClientData.has_internal_analytics_tracking || !window.POSTHOG_PUBLIC_API_KEY) {
      return null;
    }
    return posthog.get_distinct_id();
  }, [posthog]);
};

export const sendPageView = (data: PageViewData) => {
  sendDataLayerEvent({ name: CustomAnalyticsEventName.PageView, data });
};

export const useSendPostHogLoginAttempted = () => {
  const sendPostHogEvent = useSendPostHogEvent();

  return useCallback(
    (eventData: PostHogLoginAttemptedEvent["data"]) => {
      sendPostHogEvent({ name: PostHogEventName.LoginAttempted, data: eventData });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogLoginFailed = () => {
  const sendPostHogEvent = useSendPostHogEvent();

  return useCallback(
    (eventData: PostHogLoginFailedEvent["data"]) => {
      sendPostHogEvent({ name: PostHogEventName.LoginFailed, data: eventData });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogLoginSucceeded = () => {
  const sendPostHogEvent = useSendPostHogEvent();

  return useCallback(
    (eventData: PostHogLoginSucceededEvent["data"]) => {
      sendPostHogEvent({ name: PostHogEventName.LoginSucceeded, data: eventData });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogPasswordResetRequested = () => {
  const sendPostHogEvent = useSendPostHogEvent();

  return useCallback(
    (eventData: PostHogPasswordResetRequestedEvent["data"]) => {
      sendPostHogEvent({ name: PostHogEventName.PasswordResetRequested, data: eventData });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogPasswordResetRequestSucceeded = () => {
  const sendPostHogEvent = useSendPostHogEvent();

  return useCallback(
    (eventData: PostHogPasswordResetRequestSucceededEvent["data"]) => {
      sendPostHogEvent({ name: PostHogEventName.PasswordResetRequestSucceeded, data: eventData });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogPasswordResetRequestFailed = () => {
  const sendPostHogEvent = useSendPostHogEvent();

  return useCallback(
    (eventData: PostHogPasswordResetRequestFailedEvent["data"]) => {
      sendPostHogEvent({ name: PostHogEventName.PasswordResetRequestFailed, data: eventData });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogSignupCompleted = () => {
  const sendPostHogEvent = useSendPostHogEvent();

  return useCallback(
    (eventData: PostHogSignupCompletedEvent["data"]) => {
      sendPostHogEvent({ name: PostHogEventName.SignupCompleted, data: eventData });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogSignupFailed = () => {
  const sendPostHogEvent = useSendPostHogEvent();

  return useCallback(
    (eventData: PostHogSignupFailedEvent["data"]) => {
      sendPostHogEvent({ name: PostHogEventName.SignupFailed, data: eventData });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogAddToCart = () => {
  const sendPostHogEvent = useSendPostHogEvent();

  return useCallback(
    (eventData: PostHogAddToCartEvent["data"]) => {
      sendPostHogEvent({
        name: PostHogEventName.AddToCart,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogRemoveFromCart = () => {
  const sendPostHogEvent = useSendPostHogEvent();

  return useCallback(
    (eventData: PostHogRemoveFromCartEvent["data"]) => {
      sendPostHogEvent({
        name: PostHogEventName.RemoveFromCart,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogProductLineClick = () => {
  const sendPostHogEvent = useSendPostHogEvent();

  return useCallback(
    (eventData: PostHogProductLineClickEvent["data"]) => {
      sendPostHogEvent({
        name: PostHogEventName.ProductLineClick,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogProductClick = () => {
  const sendPostHogEvent = useSendPostHogEvent();

  return useCallback(
    (eventData: PostHogProductClickEvent["data"]) => {
      sendPostHogEvent({
        name: PostHogEventName.ProductClick,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

/**
 * Minimal item identifiers for the PostHog item_click event. Kept separate from
 * the GA4 ecommerce AnalyticsItemData so the PostHog and ecommerce item shapes
 * can evolve independently.
 */
type ItemClickData = {
  id: number;
  product: {
    id: number;
    product_line: { id: number };
  };
};

export const useSendPostHogItemClick = () => {
  const sendPostHogEvent = useSendPostHogEvent();

  return useCallback(
    (item: ItemClickData, opts: Pick<PostHogItemClickEvent["data"], "source" | "unit_price">) => {
      sendPostHogEvent({
        name: PostHogEventName.ItemClick,
        data: {
          source: opts.source,
          id: item.id,
          product_id: item.product.id,
          product_line_id: item.product.product_line.id,
          unit_price: opts.unit_price,
        },
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogProductsView = () => {
  const sendPostHogEvent = useSendPostHogEvent();

  return useCallback(
    (eventData: PostHogProductsViewEvent["data"]) => {
      sendPostHogEvent({
        name: PostHogEventName.ProductsView,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogItemsView = () => {
  const sendPostHogEvent = useSendPostHogEvent();

  return useCallback(
    (eventData: PostHogItemsViewEvent["data"]) => {
      sendPostHogEvent({
        name: PostHogEventName.ItemsView,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogAddToCartFromLink = () => {
  const sendPostHogEvent = useSendPostHogEvent();

  return useCallback(
    (eventData: PostHogAddToCartFromLinkEvent["data"]) => {
      sendPostHogEvent({
        name: PostHogEventName.AddToCartFromLink,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogProductLineView = () => {
  const sendPostHogEvent = useSendPostHogEvent();

  return useCallback(
    (eventData: PostHogProductLineViewEvent["data"]) => {
      sendPostHogEvent({
        name: PostHogEventName.ProductLineView,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogProductLinesView = () => {
  const sendPostHogEvent = useSendPostHogEvent();

  return useCallback(
    (eventData: PostHogProductLinesViewEvent["data"]) => {
      sendPostHogEvent({
        name: PostHogEventName.ProductLinesView,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogBeginCheckout = () => {
  const sendPostHogEvent = useSendPostHogEvent();

  return useCallback(
    (cart: ClientCartDetail) => {
      const data: PostHogBeginCheckoutEvent = {
        name: PostHogEventName.BeginCheckout,
        data: {
          cart_id: cart.id,
          client_id: cart.client_id,
          clinic_id: cart.clinic.id,
          subtotal: cart.subtotal,
          discount: cart.discount,
          shipping: cart.shipping,
          total: cart.total,
          promo_code_ids: cart.promo_codes.map((promoCode) => promoCode.id),
          cart_items: cart.items.map((cartItem) => {
            const { price, discount } = getCartItemPricing(cartItem);
            return {
              id: cartItem.id,
              item_id: cartItem.item.id,
              patient_id: cartItem.patient?.id ?? null,
              flavor_selection_id: cartItem.flavor_selection ?? null,
              quantity: cartItem.quantity,
              unit_price: price,
              discount,
            };
          }),
        },
      };
      sendPostHogEvent(data);
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogUpdateShippingMethod = () => {
  const sendPostHogEvent = useSendPostHogEvent();

  return useCallback(
    (eventData: PostHogUpdateShippingMethodEvent["data"]) => {
      sendPostHogEvent({
        name: PostHogEventName.UpdateShippingMethod,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogCheckoutCompleted = () => {
  const sendPostHogEvent = useSendPostHogEvent();

  return useCallback(
    (order: ClientCheckoutOrder, cart: ClientCartDetail) => {
      const data: PostHogCheckoutCompletedEvent = {
        name: PostHogEventName.CheckoutCompleted,
        data: {
          id: order.id,
          cart_id: cart.id,
          client_id: cart.client_id,
          clinic_id: cart.clinic.id,
          payment_method_id: cart.payment_method?.id ?? null,
          shipping_address_id: order.shipping_address?.id ?? null,
          total: order.total,
          discounted_subtotal: order.discounted_subtotal,
          tax: order.tax,
          shipping: order.shipping,
          promo_code_ids: cart.promo_codes.map((promoCode) => promoCode.id),
          order_items: order.order_items.map((orderItem) => ({
            id: orderItem.id,
            item_id: orderItem.item.id,
            patient_id: orderItem.patient?.id ?? null,
            prescription_fill_id: orderItem.prescription_fill?.id ?? null,
            quantity: orderItem.quantity,
            unit_price: orderItem.unit_price,
            amortized_discount: orderItem.amortized_discount,
            status: orderItem.status ?? null,
          })),
        },
      };
      sendPostHogEvent(data);
    },
    [sendPostHogEvent],
  );
};

export const sendPurchase = (order: ClientCheckoutOrder, cart: ClientCartDetail) => {
  const data: EcommercePurchaseEvent = {
    name: EcommerceEventName.Purchase,
    data: {
      currency: "USD",
      transaction_id: `${order.id}`,
      value: toDollarValue(order.discounted_subtotal),
      shipping: toDollarValue(order.shipping),
      tax: toDollarValue(order.tax),
      items: order.order_items.map((orderItem) => ({
        ...toEcommerceItem(orderItem.item, {
          price: orderItem.unit_price,
          discount: orderItem.amortized_discount,
        }),
        quantity: orderItem.quantity,
      })),
      coupon: cart.promo_codes.map((pc) => pc.code).join(", "),
    },
  };
  sendGtagEvent(data);
};

export const sendAddToCart = (cartItem: AnalyticsCartItem) => {
  const item = analyticsCartItemToEcommerceItem(cartItem);
  const data: EcommerceAddToCartEvent = {
    name: EcommerceEventName.AddToCart,
    data: {
      currency: "USD",
      value: toDollarValue(cartItem.price * cartItem.quantity),
      items: [item],
    },
  };
  sendGtagEvent(data);
};

export const sendRemoveFromCart = (cartItem: AnalyticsCartItem) => {
  const item = analyticsCartItemToEcommerceItem(cartItem);
  const data: EcommerceRemoveFromCartEvent = {
    name: EcommerceEventName.RemoveFromCart,
    data: {
      currency: "USD",
      value: toDollarValue(cartItem.price * cartItem.quantity),
      items: [item],
    },
  };
  sendGtagEvent(data);
};

export const sendViewItem = (data: AnalyticsItemEventData, opts: { price: number }) => {
  const item = toEcommerceItem(data, opts);
  const eventData: EcommerceViewItemEvent = {
    name: EcommerceEventName.ViewItem,
    data: {
      currency: "USD",
      value: toDollarValue(opts.price),
      items: [item],
    },
  };
  sendGtagEvent(eventData);
};

export const sendViewItemList = (items: AnalyticsItemEventData[], listName: string) => {
  const data: EcommerceViewItemListEvent = {
    name: EcommerceEventName.ViewItemList,
    data: {
      item_list_name: listName,
      items: items.map((item, i) => ({
        ...toEcommerceItem(item),
        index: i,
      })),
    },
  };
  sendGtagEvent(data);
};

/**
 * Fires a `view_item_list` GA4 event when the referenced element scrolls into view.
 * The event re-fires whenever the set of item IDs or the list name changes.
 *
 * @returns A ref to attach to the container element that should trigger the event.
 */
export const useViewItemListOnVisible = (
  items: Optional<AnalyticsItemEventData[]>,
  listName: string,
) => {
  const ref = useRef<HTMLDivElement>(null);
  const firedRef = useRef(false);

  const itemKey = items?.map((item) => item.id).join(",") ?? "";

  useEffect(() => {
    firedRef.current = false;
  }, [itemKey, listName]);

  const onIntersect = useCallback(() => {
    if (!firedRef.current && items?.length) {
      firedRef.current = true;
      sendViewItemList(items, listName);
    }
  }, [items, listName]);
  useIntersectionObserver(ref, onIntersect);
  return ref;
};

/** Minimal product identifiers for the PostHog products_view event. */
type ProductViewData = {
  id: number;
};

export const useProductsViewOnVisible = (
  products: Optional<ProductViewData[]>,
  source: PostHogProductsViewEvent["data"]["source"],
  searchTerm?: string,
) => {
  const sendPostHogProductsView = useSendPostHogProductsView();
  const ref = useRef<HTMLDivElement>(null);
  const firedRef = useRef(false);

  const productKey = products?.map((product) => product.id).join(",") ?? "";
  // Match the backend, which searches on `q.lower().strip()`, so terms group consistently.
  const normalizedSearchTerm = searchTerm?.toLowerCase().trim() || undefined;

  useEffect(() => {
    firedRef.current = false;
  }, [productKey, source]);

  const onIntersect = useCallback(() => {
    if (!firedRef.current && products?.length) {
      firedRef.current = true;
      sendPostHogProductsView({
        source,
        product_ids: products.map((product) => product.id),
        ...(normalizedSearchTerm ? { search_term: normalizedSearchTerm } : {}),
      });
    }
  }, [products, sendPostHogProductsView, source, normalizedSearchTerm]);

  useIntersectionObserver(ref, onIntersect);
  return ref;
};

type ItemViewData = {
  id: number;
};

export const useItemsViewOnVisible = (
  items: Optional<ItemViewData[]>,
  source: PostHogItemsViewEvent["data"]["source"],
) => {
  const sendPostHogItemsView = useSendPostHogItemsView();
  const ref = useRef<HTMLDivElement>(null);
  const firedRef = useRef(false);

  const itemKey = items?.map((item) => item.id).join(",") ?? "";

  useEffect(() => {
    firedRef.current = false;
  }, [itemKey, source]);

  const onIntersect = useCallback(() => {
    if (!firedRef.current && items?.length) {
      firedRef.current = true;
      sendPostHogItemsView({
        source,
        item_ids: items.map((item) => item.id),
      });
    }
  }, [items, sendPostHogItemsView, source]);

  useIntersectionObserver(ref, onIntersect);
  return ref;
};

type ProductLineViewData = {
  id: number;
};

export const useProductLinesViewOnVisible = (
  productLines: Optional<ProductLineViewData[]>,
  source: PostHogProductLinesViewEvent["data"]["source"],
  categoryId?: number,
) => {
  const sendPostHogProductLinesView = useSendPostHogProductLinesView();
  const ref = useRef<HTMLDivElement>(null);
  const firedRef = useRef(false);

  const productLineKey = productLines?.map((productLine) => productLine.id).join(",") ?? "";

  useEffect(() => {
    firedRef.current = false;
  }, [productLineKey, source]);

  const onIntersect = useCallback(() => {
    if (!firedRef.current && productLines?.length) {
      firedRef.current = true;
      sendPostHogProductLinesView({
        source,
        product_line_ids: productLines.map((productLine) => productLine.id),
        category_id: categoryId ?? null,
      });
    }
  }, [categoryId, productLines, sendPostHogProductLinesView, source]);

  useIntersectionObserver(ref, onIntersect);
  return ref;
};

/**
 * @param price — included for backwards compatibility with cart item clicks.
 *   TODO: Consider removing price for events where it doesn't make sense
 *   (e.g., product line card clicks, search result clicks).
 */
export const sendSelectItem = (data: AnalyticsItemEventData, listName: string, price?: number) => {
  const item = toEcommerceItem(data, price != null ? { price, discount: 0 } : undefined);
  const eventData: EcommerceSelectItemEvent = {
    name: EcommerceEventName.SelectItem,
    data: {
      item_list_name: listName,
      items: [item],
    },
  };
  sendGtagEvent(eventData);
};

/** Resolves ICartItem pricing and converts to EcommerceItem in one step. */
const cartItemToEcommerceItem = (cartItem: ICartItem): EcommerceItem => {
  const { price, discount } = getCartItemPricing(cartItem);
  return {
    ...toEcommerceItem(cartItem.item, { price, discount }),
    quantity: cartItem.quantity,
  };
};

export const sendBeginCheckout = (cart: ClientCartDetail) => {
  const items = cart.items.map(cartItemToEcommerceItem);
  const value = cart.items.reduce((sum, ci) => {
    const { price } = getCartItemPricing(ci);
    return sum + toDollarValue(price) * ci.quantity;
  }, 0);
  const data: EcommerceBeginCheckoutEvent = {
    name: EcommerceEventName.BeginCheckout,
    data: {
      currency: "USD",
      value,
      items,
    },
  };
  sendGtagEvent(data);
};

export const sendAddShippingInfo = (cart: ClientCartDetail) => {
  const coupon = cart.promo_codes.map((pc) => pc.code).join(", ");

  cart.delivery_groups
    .filter((g) => g.kind === DeliveryGroupKind.Shipped)
    .forEach((group) => {
      const option = group.selected_delivery_option;
      if (!option) {
        return;
      }

      const data: EcommerceAddShippingInfoEvent = {
        name: EcommerceEventName.AddShippingInfo,
        data: {
          currency: "USD",
          value: toDollarValue(cart.subtotal),
          ...(coupon ? { coupon } : {}),
          shipping_tier: option.title,
          items: group.cart_items.map((ci) => cartItemToEcommerceItem(ci)),
        },
      };
      sendGtagEvent(data);
    });
};

const getPaymentType = (paymentMethod: PaymentMethodOption): EcommercePaymentType => {
  if (paymentMethod.is_klarna) {
    return "klarna";
  }
  if (paymentMethod.wallet === WalletType.APPLE) {
    return "apple_pay";
  }
  if (paymentMethod.wallet === WalletType.GOOGLE) {
    return "google_pay";
  }
  return "credit_card";
};

export const useSendPostHogUpdatePaymentMethod = () => {
  const sendPostHogEvent = useSendPostHogEvent();

  return useCallback(
    (cart: ClientCartDetail, paymentMethod: PaymentMethodOption) => {
      const data: PostHogUpdatePaymentMethodEvent = {
        name: PostHogEventName.UpdatePaymentMethod,
        data: {
          cart_id: cart.id,
          client_id: cart.client_id,
          clinic_id: cart.clinic.id,
          payment_method_id: typeof paymentMethod.id === "number" ? paymentMethod.id : null,
          payment_type: getPaymentType(paymentMethod),
          wallet_type: paymentMethod.wallet || null,
          card_brand: "brand" in paymentMethod ? paymentMethod.brand : null,
        },
      };
      sendPostHogEvent(data);
    },
    [sendPostHogEvent],
  );
};

export const sendAddPaymentInfo = (cart: ClientCartDetail, paymentMethod: PaymentMethodOption) => {
  const coupon = cart.promo_codes.map((pc) => pc.code).join(", ");

  const data: EcommerceAddPaymentInfoEvent = {
    name: EcommerceEventName.AddPaymentInfo,
    data: {
      currency: "USD",
      value: toDollarValue(cart.subtotal),
      ...(coupon ? { coupon } : {}),
      payment_type: getPaymentType(paymentMethod),
      items: cart.items.map((ci) => cartItemToEcommerceItem(ci)),
    },
  };
  sendGtagEvent(data);
};
